import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { X, Plus, ExternalLink } from "lucide-react";

const API_URL =
  process.env.REACT_APP_SEARCH_API ||
  "https://pnrnzdmabwlldxtrpujo.supabase.co/functions/v1/product-search";

const ComparePage = () => {
  const [compareList, setCompareList] = useState(() => {
    const saved = localStorage.getItem("compareList");
    return saved ? JSON.parse(saved) : [];
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchProducts = async () => {
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      const response = await fetch(
        `${API_URL}?name=${encodeURIComponent(searchTerm)}&limit=24`
      );
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.products);
      }
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setSearching(false);
    }
  };

  const addToCompare = (product) => {
    if (compareList.length >= 4) {
      alert("Maximum 4 products can be compared");
      return;
    }
    if (compareList.find((p) => p.sku === product.sku)) {
      alert("Product already in comparison");
      return;
    }
    const newList = [...compareList, product];
    setCompareList(newList);
    localStorage.setItem("compareList", JSON.stringify(newList));
    setSearchResults([]);
    setSearchTerm("");
  };

  const removeFromCompare = (sku) => {
    const newList = compareList.filter((p) => p.sku !== sku);
    setCompareList(newList);
    localStorage.setItem("compareList", JSON.stringify(newList));
  };

  const clearAll = () => {
    setCompareList([]);
    localStorage.removeItem("compareList");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-50 dark:via-gray-100 dark:to-gray-50">
      <Helmet>
        <title>Compare Optics Side-by-Side | OpticPicker</title>
        <meta name="description" content="Compare rifle scopes, red dots, and optics side-by-side. Check specs, prices, and features across multiple products at once." />
        <meta property="og:title" content="Compare Optics Side-by-Side | OpticPicker" />
        <meta property="og:url" content="https://www.opticpicker.com/compare" />
        <link rel="canonical" href="https://www.opticpicker.com/compare" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white dark:text-gray-900 mb-2">
            Compare Optics
          </h1>
          <p className="text-gray-400 dark:text-gray-600">
            Compare up to 4 products side-by-side
          </p>
        </div>

        <div className="mb-8 bg-gray-800/60 dark:bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 dark:border-gray-300">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search products to add to comparison..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  searchProducts();
                }
              }}
              className="flex-1 px-4 py-3 bg-gray-700/50 dark:bg-gray-100 border border-gray-600/50 dark:border-gray-300 rounded-xl text-white dark:text-gray-900 focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={searchProducts}
              disabled={searching}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition disabled:opacity-50"
            >
              {searching ? "Searching..." : "Search"}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="mt-4 max-h-96 overflow-y-auto space-y-2">
              {searchResults.map((product) => (
                <div
                  key={product.sku}
                  className="flex items-center justify-between p-4 bg-gray-700/50 dark:bg-gray-100 rounded-lg hover:bg-gray-600/50 dark:hover:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={
                        product.imageUrl ||
                        "https://dummyimage.com/80x80/1f2937/9ca3af"
                      }
                      alt={product.name}
                      className="w-16 h-16 object-contain rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-white dark:text-gray-900">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-600">
                        {product.manufacturer}
                      </p>
                      <p className="text-sm font-bold text-green-400 dark:text-green-600">
                        ${product.currentPrice.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => addToCompare(product)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {compareList.length === 0 ? (
          <div className="text-center py-20 bg-gray-800/40 dark:bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-700/50 dark:border-gray-300">
            <Plus className="w-16 h-16 text-gray-600 dark:text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-400 dark:text-gray-600">
              No products selected for comparison
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
              Search and add products above to start comparing
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-400 dark:text-gray-600">
                Comparing {compareList.length} of 4 products
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-red-400 hover:text-red-300 dark:text-red-600 dark:hover:text-red-500"
              >
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {compareList.map((product) => {
                const stockClassName = product.inStock
                  ? "text-green-400 dark:text-green-600"
                  : "text-red-400 dark:text-red-600";
                const stockText = product.inStock ? "In Stock" : "Out of Stock";

                return (
                  <div
                    key={product.sku}
                    className="bg-gray-800/60 dark:bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 dark:border-gray-300 overflow-hidden"
                  >
                    <div className="relative">
                      <button
                        onClick={() => removeFromCompare(product.sku)}
                        className="absolute top-2 right-2 z-10 p-1 bg-red-500/90 hover:bg-red-600 rounded-full"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      <img
                        src={
                          product.imageUrl ||
                          "https://dummyimage.com/400x300/1f2937/9ca3af"
                        }
                        alt={product.name}
                        className="w-full h-48 object-contain bg-gray-900/50 dark:bg-gray-100"
                      />
                    </div>

                    <div className="p-4">
                      <div className="mb-3">
                        <span className="inline-block px-2 py-1 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded mb-2">
                          {product.manufacturer}
                        </span>
                        <h3 className="text-sm font-bold text-white dark:text-gray-900 line-clamp-2 mb-1">
                          {product.name}
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-gray-600">
                          {product.detectedCategory}
                        </p>
                      </div>

                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between border-b border-gray-700 dark:border-gray-300 pb-1">
                          <span className="text-gray-400 dark:text-gray-600">
                            Price
                          </span>
                          <span className="font-bold text-green-400 dark:text-green-600">
                            ${product.currentPrice.toFixed(2)}
                          </span>
                        </div>
                        {product.magnification && (
                          <div className="flex justify-between border-b border-gray-700 dark:border-gray-300 pb-1">
                            <span className="text-gray-400 dark:text-gray-600">
                              Magnification
                            </span>
                            <span className="text-white dark:text-gray-900">
                              {product.magnification}
                            </span>
                          </div>
                        )}
                        {product.moa && (
                          <div className="flex justify-between border-b border-gray-700 dark:border-gray-300 pb-1">
                            <span className="text-gray-400 dark:text-gray-600">
                              MOA
                            </span>
                            <span className="text-white dark:text-gray-900">
                              {product.moa}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between border-b border-gray-700 dark:border-gray-300 pb-1">
                          <span className="text-gray-400 dark:text-gray-600">
                            Stock
                          </span>
                          <span className={stockClassName}>{stockText}</span>
                        </div>
                        <div className="flex justify-between border-b border-gray-700 dark:border-gray-300 pb-1">
                          <span className="text-gray-400 dark:text-gray-600">
                            MPN
                          </span>
                          <span className="text-white dark:text-gray-900 text-right">
                            {product.mpn}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 space-y-2">
                        {product.productUrl && (
                          <a
                            href={product.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition"
                          >
                            <span>View at Euro Optic</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {product.psaUrl && (
                          <a
                            href={product.psaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between w-full px-3 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs font-medium transition"
                          >
                            <span>View at PSA</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
